import { MAP_ZOOM_LEVEL, MY_LOCATION_ACTIVE_ID, MY_LOCATION_ANIMATE, MY_LOCATION_MARKER, LEGACY_MY_LOCATION_MARKER, isSiheungVerticalMyLocationEnabled } from '@/constants/floor-guide';
import { useCalculateIconSize } from '@/hooks/floor-guide';
import { useOrientation } from '@/hooks/useOrientation';
import { getMapsInstance } from '@/lib/map';
import { useGetMapDataQuery } from '@/services/queries/map';
import { useConfigStore } from '@/stores';
import { useMarkerStore } from '@/stores/markerStore';
import type { MapDataPayload } from '@/types/map';
import { generateMapLanguage } from '@/utils/floor-guide';
import {
    calculateMyLocationPulseIconSize,
    mapMyLocationPinMarker,
    mapMyLocationPulseMyLocOption,
    mapMyLocationToMarker,
} from '@/utils/marker';
import { calculateZoomAwareScreenIconSize } from '@/utils/marker-size';
import { CAMERA_TYPE, LANG_TYPE, type MapOptions } from 'dabeeomaps';
import type { DabeeoMap } from 'dabeeomaps/dist/src/DabeeoMap';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type Error = {
    isError: boolean;
    message: string;
};

export const useMap = <T extends HTMLElement>(config: MapDataPayload) => {
    const currentMap = useRef<DabeeoMap | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error>({
        isError: false,
        message: '',
    });
    const [isMapReady, setIsMapReady] = useState<boolean>(false);
    const machine = useConfigStore((state) => state?.config?.server?.machine);
    const floorName = useConfigStore((state) => state?.config?.server?.floor?.name);
    const floorCode = useConfigStore((state) => state?.config?.server?.floor?.floorCode);
    const language = useConfigStore((state) => state?.config?.ui?.language);
    const buildingId = useConfigStore((state) => state?.config?.server?.building?.id) || '';
    const storeCode = useConfigStore((state) => state?.config?.server?.store?.storeCode) ?? '';
    const { isHorizontal } = useOrientation();

    const { setMarkerIds } = useMarkerStore();

    const { calculateUniformIconSize, calculateIconSize } = useCalculateIconSize();

    const { data: mapData, isLoading, isError } = useGetMapDataQuery(config);

    // Use refs for values that should NOT trigger map re-creation
    const machineRef = useRef(machine);
    machineRef.current = machine;
    const floorNameRef = useRef(floorName);
    floorNameRef.current = floorName;
    const floorCodeRef = useRef(floorCode);
    floorCodeRef.current = floorCode;
    const languageRef = useRef(language);
    languageRef.current = language;
    const calculateUniformIconSizeRef = useRef(calculateUniformIconSize);
    calculateUniformIconSizeRef.current = calculateUniformIconSize;
    const calculateIconSizeRef = useRef(calculateIconSize);
    calculateIconSizeRef.current = calculateIconSize;
    const setMarkerIdsRef = useRef(setMarkerIds);
    setMarkerIdsRef.current = setMarkerIds;
    const isSiheungVerticalMyLocationRef = useRef(false);
    isSiheungVerticalMyLocationRef.current = isSiheungVerticalMyLocationEnabled(storeCode, isHorizontal);

    const getFloorsByBuildingId = useCallback(() => {
        const buildings = mapData?.dataBuilding?.getBuildings?.();
        if (!buildings) {
            return [];
        }

        return buildings.find((building) => buildingId.includes(building.id))?.floors || [];
    }, [mapData?.dataBuilding, buildingId]);

    const getFloorsByBuildingIdRef = useRef(getFloorsByBuildingId);
    getFloorsByBuildingIdRef.current = getFloorsByBuildingId;

    const initMap = useCallback(
        async (container: RefObject<T | null>, options?: Partial<MapOptions>) => {
            try {
                setLoading(true);
                setIsMapReady(false);
                setError({ isError: false, message: '' });

                if (!container.current) {
                    setError({ isError: true, message: 'Map container is not initialized' });
                    currentMap.current = undefined;
                    setIsMapReady(false);
                    setLoading(false);
                    return;
                }

                if (!mapData) {
                    setError({ isError: true, message: 'Map data is not available' });
                    currentMap.current = undefined;
                    setIsMapReady(false);
                    setLoading(false);
                    return;
                }

                // Read current values from refs at call time
                const currentMachine = machineRef.current;
                const currentFloorCode = floorCodeRef.current;
                const currentLanguage = languageRef.current;
                const currentCalculateUniformIconSize = calculateUniformIconSizeRef.current;
                const currentCalculateIconSize = calculateIconSizeRef.current;
                const currentSetMarkerIds = setMarkerIdsRef.current;

                currentMap.current = await getMapsInstance().showMap(
                    container.current,
                    {
                        camera: CAMERA_TYPE.D2,
                        enableGeoreferencing: false,
                        showWaterMarker: false,
                        framerate: 30,
                        spriteEnable: true,
                        spriteKeepRotation: true,
                        controlOption: {
                            zoom: currentMachine?.zoom,
                            rotate: currentMachine?.rotate,
                            pan: {
                                x: currentMachine?.positionX,
                                y: currentMachine?.positionY,
                            },
                        },
                        ...options,
                        floor: currentFloorCode,
                    },
                    mapData
                );

                if (!currentMap.current) {
                    setError({ isError: true, message: 'Failed to initialize map' });
                    setIsMapReady(false);
                    setLoading(false);
                    throw new Error('Failed to initialize map');
                }

                currentMap.current?.control?.setOption({
                    touchOption: { enableRotate: false },
                    mouseOption: { enableRotate: false },
                    controlRangeOption: {
                        zoom: { min: MAP_ZOOM_LEVEL.MIN, max: MAP_ZOOM_LEVEL.MAX },
                    },
                });


                currentMap.current?.context?.changeLanguage?.(generateMapLanguage(currentLanguage) as LANG_TYPE);

                if (isSiheungVerticalMyLocationRef.current && currentFloorCode && currentMachine) {
                    const screenSize = currentCalculateUniformIconSize(MY_LOCATION_MARKER.BASE_SIZE);
                    const currentZoom = currentMachine?.zoom ?? MY_LOCATION_MARKER.REFERENCE_ZOOM;
                    const iconSize = calculateZoomAwareScreenIconSize({
                        screenIconSize: screenSize,
                        referenceZoom: MY_LOCATION_MARKER.REFERENCE_ZOOM,
                        currentZoom,
                        minVisiblePx: MY_LOCATION_MARKER.MIN_VISIBLE_PX,
                    });
                    const myLocationOption = mapMyLocationPinMarker(
                        currentFloorCode,
                        currentMachine,
                        iconSize
                    );

                    const pinMarkerIds = await currentMap.current?.markers?.set?.({ marker: [myLocationOption] });
                    const pinMarkerId = Array.isArray(pinMarkerIds) ? pinMarkerIds[0] : undefined;

                    if (pinMarkerId) {
                        const basePulseScreenSize = currentCalculateUniformIconSize({
                            width: MY_LOCATION_MARKER.PULSE_SIZE,
                            height: MY_LOCATION_MARKER.PULSE_SIZE,
                        });
                        const pulseScreenSize = calculateZoomAwareScreenIconSize({
                            screenIconSize: basePulseScreenSize,
                            referenceZoom: MY_LOCATION_MARKER.REFERENCE_ZOOM,
                            currentZoom,
                            minVisiblePx: MY_LOCATION_MARKER.MIN_VISIBLE_PX,
                        });
                        const pulseIconSize = calculateMyLocationPulseIconSize(pulseScreenSize);
                        const pulseOption = mapMyLocationPulseMyLocOption(currentFloorCode, currentMachine, pulseIconSize);
                        await currentMap.current?.mylocation?.set?.(pulseOption);
                        currentSetMarkerIds({
                            myLocation: pinMarkerId,
                            myLocationPulse: MY_LOCATION_ACTIVE_ID,
                        });
                        setTimeout(() => {
                            currentMap.current?.mylocation?.clear?.();
                            currentSetMarkerIds({ myLocationPulse: undefined });
                        }, MY_LOCATION_ANIMATE.DURATION * MY_LOCATION_ANIMATE.REPEAT + 100);
                    }
                } else if (currentFloorCode && currentMachine) {
                    const currentZoom = currentMachine.zoom ?? LEGACY_MY_LOCATION_MARKER.REFERENCE_ZOOM;
                    const baseScreenSize = currentCalculateIconSize(LEGACY_MY_LOCATION_MARKER.BASE_SIZE);
                    const legacyIconSize = calculateZoomAwareScreenIconSize({
                        screenIconSize: baseScreenSize,
                        referenceZoom: LEGACY_MY_LOCATION_MARKER.REFERENCE_ZOOM,
                        currentZoom,
                        minVisiblePx: LEGACY_MY_LOCATION_MARKER.MIN_VISIBLE_PX,
                    });
                    const legacyMarker = mapMyLocationToMarker(currentFloorCode, currentMachine, legacyIconSize);
                    if (legacyMarker) {
                        const legacyMarkerIds = await currentMap.current?.markers?.set?.({ marker: [legacyMarker] });
                        currentSetMarkerIds({
                            myLocation: Array.isArray(legacyMarkerIds) ? legacyMarkerIds[0] : undefined,
                        });
                    }
                }

                currentMap.current?.control?.moveTo?.({
                    position: {
                        x: currentMachine?.positionX ?? 0,
                        y: currentMachine?.positionY ?? 0,
                    },
                    floorId: currentFloorCode,
                    transition: true,
                });

                setError({ isError: false, message: '' });
                setIsMapReady(true);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                setError({ isError: true, message: errorMessage });
                setIsMapReady(false);
            } finally {
                setLoading(false);
            }
        },
        [mapData]
    );

    const cleanupMap = useCallback(() => {
        if (currentMap.current?.context?.cleanup) {
            currentMap.current.context.cleanup();
            currentMap.current = undefined;
            setIsMapReady(false);
        }
    }, []);

    useEffect(() => {
        if (isError) {
            setError({ isError: true, message: 'Map data is not available' });
            setIsMapReady(false);
        }
    }, [isError]);

    return {
        initMap,
        cleanupMap,
        currentMap: currentMap.current,
        mapData,
        loading: isLoading || loading,
        error,
        isMapReady,
    };
};
